// ========================================
// THRESHOLD UTILITIES
// Manages configurable alert thresholds stored in localStorage
// ========================================

// Default threshold values
export const DEFAULT_THRESHOLDS = {
    // Budget thresholds (per-team, can be overridden)
    BUDGET_ALERT: 0.9,          // 90% - when to show alert/warning
    BUDGET_BLOCK: 1.0,          // 100% - when to auto-block team

    // Health thresholds (global)
    HEALTH_WARNING: 0.1,        // 10% unhealthy models for warning
    HEALTH_CRITICAL: 0.3,       // 30% unhealthy models for critical
};

// localStorage keys
const STORAGE_KEYS = {
    GLOBAL_THRESHOLDS: 'dashboard_thresholds',
    TEAM_THRESHOLDS_PREFIX: 'team_thresholds_',
};

// ========================================
// GLOBAL THRESHOLDS
// ========================================

/**
 * Get global threshold settings
 * @returns {Object} Global thresholds with defaults applied
 */
export const getGlobalThresholds = () => {
    try {
        const stored = localStorage.getItem(STORAGE_KEYS.GLOBAL_THRESHOLDS);
        if (stored) {
            const parsed = JSON.parse(stored);
            return {
                budgetAlert: parsed.budgetAlert ?? DEFAULT_THRESHOLDS.BUDGET_ALERT,
                budgetBlock: parsed.budgetBlock ?? DEFAULT_THRESHOLDS.BUDGET_BLOCK,
                healthWarning: parsed.healthWarning ?? DEFAULT_THRESHOLDS.HEALTH_WARNING,
                healthCritical: parsed.healthCritical ?? DEFAULT_THRESHOLDS.HEALTH_CRITICAL,
            };
        }
    } catch (err) {
        console.error('[THRESHOLDS] Error reading global thresholds:', err);
    }

    return {
        budgetAlert: DEFAULT_THRESHOLDS.BUDGET_ALERT,
        budgetBlock: DEFAULT_THRESHOLDS.BUDGET_BLOCK,
        healthWarning: DEFAULT_THRESHOLDS.HEALTH_WARNING,
        healthCritical: DEFAULT_THRESHOLDS.HEALTH_CRITICAL,
    };
};

/**
 * Save global threshold settings
 * @param {Object} thresholds - Threshold values to save
 */
export const saveGlobalThresholds = (thresholds) => {
    try {
        const toSave = {
            budgetAlert: thresholds.budgetAlert ?? DEFAULT_THRESHOLDS.BUDGET_ALERT,
            budgetBlock: thresholds.budgetBlock ?? DEFAULT_THRESHOLDS.BUDGET_BLOCK,
            healthWarning: thresholds.healthWarning ?? DEFAULT_THRESHOLDS.HEALTH_WARNING,
            healthCritical: thresholds.healthCritical ?? DEFAULT_THRESHOLDS.HEALTH_CRITICAL,
        };
        localStorage.setItem(STORAGE_KEYS.GLOBAL_THRESHOLDS, JSON.stringify(toSave));
        return true;
    } catch (err) {
        return false;
    }
};

// ========================================
// PER-TEAM THRESHOLDS
// ========================================

/**
 * Get threshold settings for a specific team
 * Falls back to global thresholds if not set
 * @param {string} teamId - Team ID
 * @returns {Object} Team thresholds with fallbacks
 */
export const getTeamThresholds = (teamId) => {
    const globalThresholds = getGlobalThresholds();

    if (!teamId) {
        return {
            budgetAlert: globalThresholds.budgetAlert,
            budgetBlock: globalThresholds.budgetBlock,
            useCustom: false,
        };
    }

    try {
        const stored = localStorage.getItem(STORAGE_KEYS.TEAM_THRESHOLDS_PREFIX + teamId);
        if (stored) {
            const parsed = JSON.parse(stored);
            if (parsed.useCustom) {
                return {
                    budgetAlert: parsed.budgetAlert ?? globalThresholds.budgetAlert,
                    budgetBlock: parsed.budgetBlock ?? globalThresholds.budgetBlock,
                    useCustom: true,
                };
            }
        }
    } catch (err) {
        // Fall through to return global defaults
    }

    return {
        budgetAlert: globalThresholds.budgetAlert,
        budgetBlock: globalThresholds.budgetBlock,
        useCustom: false,
    };
};

/**
 * Save threshold settings for a specific team
 * @param {string} teamId - Team ID
 * @param {Object} thresholds - Threshold values to save
 */
export const saveTeamThresholds = (teamId, thresholds) => {
    if (!teamId) {
        console.error('[THRESHOLDS] Cannot save team thresholds without teamId');
        return false;
    }

    try {
        const toSave = {
            budgetAlert: thresholds.budgetAlert,
            budgetBlock: thresholds.budgetBlock,
            useCustom: thresholds.useCustom ?? true,
        };
        localStorage.setItem(STORAGE_KEYS.TEAM_THRESHOLDS_PREFIX + teamId, JSON.stringify(toSave));
        return true;
    } catch (err) {
        return false;
    }
};

/**
 * Clear custom thresholds for a team (revert to global defaults)
 * @param {string} teamId - Team ID
 */
export const clearTeamThresholds = (teamId) => {
    if (!teamId) return false;

    try {
        localStorage.removeItem(STORAGE_KEYS.TEAM_THRESHOLDS_PREFIX + teamId);
        return true;
    } catch (err) {
        return false;
    }
};

// ========================================
// HELPER FUNCTIONS
// ========================================

/**
 * Check if a team's budget usage exceeds the alert threshold
 * @param {Object} team - Team object with spend and max_budget
 * @returns {boolean} True if alert should be shown
 */
export const isTeamBudgetAlert = (team) => {
    if (!team) return false;

    const spend = team.spend || 0;
    const maxBudget = team.max_budget || 0;

    if (maxBudget <= 0) return false;

    const thresholds = getTeamThresholds(team.team_id);
    return spend >= maxBudget * thresholds.budgetAlert;
};

/**
 * Check if a team should be auto-blocked due to budget
 * @param {Object} team - Team object with spend and max_budget
 * @returns {boolean} True if team should be blocked
 */
export const shouldBlockTeam = (team) => {
    if (!team) return false;

    const spend = team.spend || 0;
    const maxBudget = team.max_budget || 0;

    if (maxBudget <= 0) return false;

    const thresholds = getTeamThresholds(team.team_id);
    return spend >= maxBudget * thresholds.budgetBlock;
};

/**
 * Get the budget usage percentage for display
 * @param {Object} team - Team object
 * @returns {number} Usage percentage (0-100+)
 */
export const getTeamBudgetUsagePercent = (team) => {
    if (!team) return 0;

    const spend = team.spend || 0;
    const maxBudget = team.max_budget || 0;

    if (maxBudget <= 0) return 0;

    return Math.round((spend / maxBudget) * 100);
};

/**
 * Format threshold as percentage string
 * @param {number} threshold - Threshold value (0-1)
 * @returns {string} Formatted percentage
 */
export const formatThresholdPercent = (threshold) => {
    return `${Math.round(threshold * 100)}%`;
};

/**
 * Parse percentage input to threshold value
 * @param {string|number} input - User input (e.g., "90" or 90)
 * @returns {number} Threshold value (0-1)
 */
export const parsePercentToThreshold = (input) => {
    const num = parseFloat(input);
    if (isNaN(num)) return DEFAULT_THRESHOLDS.BUDGET_ALERT;
    return Math.max(0, Math.min(1, num / 100));
};

// ========================================
// STORAGE CLEANUP UTILITIES
// ========================================

/**
 * Clean up orphaned team thresholds that no longer have corresponding teams
 * @param {Array} currentTeamIds - Array of current valid team IDs
 * @returns {Object} Cleanup results { removed: number, remaining: number }
 */
export const cleanupOrphanedThresholds = (currentTeamIds = []) => {
    const teamIdSet = new Set(currentTeamIds);
    const keysToRemove = [];

    try {
        // Find all team threshold keys in localStorage
        for (let i = 0; i < localStorage.length; i++) {
            const key = localStorage.key(i);
            if (key && key.startsWith(STORAGE_KEYS.TEAM_THRESHOLDS_PREFIX)) {
                const teamId = key.replace(STORAGE_KEYS.TEAM_THRESHOLDS_PREFIX, '');
                if (!teamIdSet.has(teamId)) {
                    keysToRemove.push(key);
                }
            }
        }

        // Remove orphaned keys
        keysToRemove.forEach(key => {
            localStorage.removeItem(key);
        });

        return {
            removed: keysToRemove.length,
            remaining: currentTeamIds.length
        };
    } catch (err) {
        return { removed: 0, remaining: 0 };
    }
};

/**
 * Get storage usage statistics for thresholds
 * @returns {Object} Storage stats { globalSize, teamCount, totalSize }
 */
export const getThresholdsStorageStats = () => {
    try {
        let teamCount = 0;
        let totalSize = 0;

        const globalData = localStorage.getItem(STORAGE_KEYS.GLOBAL_THRESHOLDS);
        const globalSize = globalData ? new Blob([globalData]).size : 0;
        totalSize += globalSize;

        for (let i = 0; i < localStorage.length; i++) {
            const key = localStorage.key(i);
            if (key && key.startsWith(STORAGE_KEYS.TEAM_THRESHOLDS_PREFIX)) {
                teamCount++;
                const data = localStorage.getItem(key);
                if (data) {
                    totalSize += new Blob([data]).size;
                }
            }
        }

        return {
            globalSize,
            teamCount,
            totalSize,
            formatted: {
                global: `${globalSize} bytes`,
                total: `${totalSize} bytes`,
                teams: teamCount
            }
        };
    } catch (err) {
        console.error('[THRESHOLDS] Error getting storage stats:', err);
        return { globalSize: 0, teamCount: 0, totalSize: 0 };
    }
};
